import { h } from 'preact';
import Section from 'components/bolt/section/section';
import Store from 'store/store';
import 'store/bolt/synopsis';
import { accessNested, joinClasses, sortObjectByOrder } from 'utils/utils';
// import { getFileWithoutExtension } from 'utils/bolt';
import { getHeaderHeight } from 'utils/bolt';
import { getCopy } from 'utils/copy';
import Button from 'components/bolt/button/button';
import Fullscreen from 'components/bolt/icons/fullscreen';
import Download from 'components/bolt/icons/download';
import HTML from 'components/core/html/html.js';
import BurgerMenu from 'components/bolt/burgerMenu/burgerMenu';
import { sections } from 'pages/bolt_pages';
import * as webp from 'utils/bolt/webp';
import { useDynamicAssetFolder } from 'utils/assets';
import s from 'components/bolt/synopsis/synopsis.sss';

const MOBILE_CONTENT_HEIGHT_THRESHOLD = 340;
const DESKTOP_CONTENT_HEIGHT_THRESHOLD = 368;
const MINIMUM_HIDDEN_CONTENT = 64; // how much taller than the the threshold the content needs to be to show the "show more" button

export default class Synopsis extends Section {
	constructor(props) {
		super(props);
		this.state = {
			showMoreVisible: false,
			expanded: false,
		};
		this.downloadOnesheet = this.downloadOnesheet.bind(this);
		this.toggleOnesheetFullScreen = this.toggleOnesheetFullScreen.bind(this);
		this.toggleSynopsis = this.toggleSynopsis.bind(this);
		this.getFallbackFormat = this.getFallbackFormat.bind(this);
		this.checkIfFooterShadowNeeded = this.checkIfFooterShadowNeeded.bind(this);
	}

	componentDidMount() {
		const currentPage = accessNested(this.props, ['appData', 'pages', this.props.id]) || {};
		const data = currentPage.data || {};
		this.hasTagline = accessNested(this.props, ['appData', 'copy', 'baseCopy', data.tagline], null) || data.tagline;
		Store.on('SECTION_LOADED', this.checkIfFooterShadowNeeded);

		this.checkIfFooterShadowNeeded();
		this.setDetailMinHeight();
	}

	componentWillUnmount() {
		Store.emit('ADD_FOOTER_SHADOW', false);
		Store.off('SECTION_LOADED', this.checkIfFooterShadowNeeded);
	}

	componentDidUpdate(prevProps) {
		const prevClientWidth = accessNested(prevProps, 'client.dimensions.width');
		const clientWidth = accessNested(this.props, 'client.dimensions.width');
		if (prevClientWidth !== clientWidth) {
			this.checkIfFooterShadowNeeded();
			this.setDetailMinHeight();
		}
	}

	checkIfFooterShadowNeeded() {
		const clientWidth = accessNested(this.props, 'client.dimensions.width');
		const sectionIndex = sections.findIndex((e) => e.id === this.props?.id);
		let isLastSection = sectionIndex === sections.length - 1;

		if (!isLastSection && sectionIndex === sections.length - 2) {
			// In case there is an empty section below
			const lastSectionId = sections[sections.length - 1].id;
			const lastSectionEmpty = !document.querySelector(`[data-section=${lastSectionId}]`);
			if (lastSectionEmpty) {
				isLastSection = true;
			}
		}

		if (isLastSection && this.hasTagline && clientWidth >= 1024) {
			Store.emit('ADD_FOOTER_SHADOW', true);
		} else {
			Store.emit('ADD_FOOTER_SHADOW', false);
		}
	}

	setDetailMinHeight() {
		const currentPage = accessNested(this.props, ['appData', 'pages', this.props.id]) || {};
		const data = currentPage.data || {};
		const clientWidth = accessNested(this.props, 'client.dimensions.width');
		const isFullPageWithTagline = !!(this.props.sectionFullPage && data.tagline);
		if (clientWidth < 1024 || !isFullPageWithTagline) {
			this.setState({ detailMinHeight: 0 });
			return;
		}

		const clientHeight = accessNested(this.props, 'client.dimensions.height');
		const taglineHeight = this.$tagline.clientHeight;
		const detailMinHeight = clientHeight - taglineHeight;
		this.setState({ detailMinHeight });
	}

	getHeights() {
		const heights = {
			maxHeight: 'none',
			largeContent: false,
		};
		if (!this.$content) {
			return heights;
		}

		const isMobile = accessNested(this.props, 'client.deviceSize', 'mobile') === 'mobile';
		let threshold = isMobile ? MOBILE_CONTENT_HEIGHT_THRESHOLD : DESKTOP_CONTENT_HEIGHT_THRESHOLD;
		let contentHeight = window.getComputedStyle(this.$content).height || '';
		contentHeight = contentHeight.slice(0, -2);
		const { expanded } = this.state;

		if (contentHeight - threshold >= MINIMUM_HIDDEN_CONTENT) {
			heights.largeContent = true;
			heights.maxHeight = expanded ? contentHeight + 'px' : threshold + 'px';
		}

		return heights;
	}

	toggleSynopsis() {
		if (!this.state.expanded) {
			// If the focus is on the "show more" button after you clicked, the page will scroll down as the content expands.
			// Removing the focus keeps the page where it is
			document.activeElement.blur();
		}
		this.setState({ expanded: !this.state.expanded });
	}

	toggleOnesheetFullScreen() {
		const onesheetExpanded = accessNested(this.props, 'synopsis.onesheetExpanded');

		if (!onesheetExpanded) {
			const imageUrl = accessNested(this.props, `appData.pages.${this.props.id}.data.onesheet`, null);
			const imageName = imageUrl.split('/').pop();
			Store.emit('SYNOPSIS_ENLARGE_IMAGE', imageName);
			Store.emit('TOGGLE_ONESHEET', this.props.id);
		} else {
			Store.emit('TOGGLE_ONESHEET', null);
		}
	}

	stopPropagation(e) {
		if (e) {
			e.stopPropagation();
		}
	}

	async downloadOnesheet() {
		if (!this.$onesheet) {
			return;
		}
		let image = this.$onesheet;
		if (image.base) {
			image = image.base;
		}
		const movieSlug = accessNested(this.props, 'appData.meta.title.localeSlug', 'poster');
		const isIOS = accessNested(this.props, 'client.isIOS', false);
		const isEdge = accessNested(this.props, 'client.isEdge', false);

		const link = document.createElement('a');
		link.download = `${movieSlug}.jpg`;
		link.href = accessNested(this.props, `appData.pages.${this.props.id}.data.onesheet`, null);

		Store.emit('SYNOPSIS_DOWNLOAD_IMAGE', link.href.split('/').pop());

		let imageCopy;

		if (!isIOS) {
			imageCopy = await new Promise((resolve, reject) => {
				const imageCopy = document.createElement('img');
				imageCopy.crossOrigin = 'anonymous';
				imageCopy.src = image.src + (image.src.includes('?') ? '&' : '?') + '_cors=1';
				imageCopy.onload = () => resolve(imageCopy);
				imageCopy.onerror = reject;
			});
		}

		if (isIOS) {
			link.target = '_blank';
		} else if (!isEdge) {
			const c = document.createElement('canvas');
			const context = c.getContext('2d');
			c.width = imageCopy.naturalWidth;
			c.height = imageCopy.naturalHeight;
			context.drawImage(imageCopy, 0, 0);
			link.href = c.toDataURL('image/jpeg');
			link.style.display = 'none';
		}

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}

	renderExpandedOnesheet(onesheet, onesheetAltText) {
		const onesheetExpanded = accessNested(this.props, 'synopsis.onesheetExpanded') == this.props.id;
		const store = Store.get();
		const sonyHeaderEnabled = accessNested(store, 'appData.options.sonyFeatures.sonyHeader', false);
		const evidon = accessNested(store, 'evidon.bannerOpen', false);
		const fallbackFormat = this.getFallbackFormat();

		let paddingTop = 0;

		if (sonyHeaderEnabled) {
			paddingTop += 34;
		}
		if (evidon) {
			paddingTop += accessNested(store, 'evidon.height', 0);
		}

		return (
			<div
				class={joinClasses(s.onesheetFullScreen, onesheetExpanded && s.visible)}
				onClick={this.toggleOnesheetFullScreen}
				style={{ paddingTop: paddingTop + 'px' }}
			>
				<BurgerMenu type={onesheetExpanded ? 'close' : 'burger'} oneSheetOverlay extraClass={s.oneSheetOverlayCloseButton} />
				<webp.Picture src={onesheet} alt={onesheetAltText} fallbackFormat={fallbackFormat} onClick={this.stopPropagation} />
			</div>
		);
	}

	renderDetails(pageData, baseCopy) {
		const content = this.content || baseCopy[pageData.content] || pageData.content;

		const expanded = this.state.expanded;
		const showMoreCopy = accessNested(this.props, 'appData.copy.baseCopy.$SHOW_MORE', 'Show more');
		const showLessCopy = accessNested(this.props, 'appData.copy.baseCopy.$SHOW_LESS', 'Show less');
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables') || {};
		const toggleSynopsis = expanded ? showLessCopy : showMoreCopy;
		const heights = this.getHeights();

		return (
			<div class={joinClasses(s.contentContainer, !heights.largeContent && s.centeredContent)}>
				<div class={s.content} style={{ maxHeight: heights.maxHeight }}>
					<div class={s.synopsisText} ref={(ref) => (this.$content = ref)} role="document">
						{content && <HTML class={joinClasses(s.body, fontStyles.synopsisBody)} markdown content={content} />}
						{this.renderCredits(pageData, baseCopy)}
					</div>
					{heights.largeContent && <div class={joinClasses(s.fade, expanded && s.expanded)} />}
				</div>
				{heights.largeContent && <div class={s.divider} />}
				{heights.largeContent && (
					<div class={joinClasses(s.showMore, fontStyles.synopsisExpand)}>
						<Button trigger={this.toggleSynopsis}>
							<div>{toggleSynopsis}</div>
						</Button>
					</div>
				)}
			</div>
		);
	}

	renderOnesheetButtons(enlarge, download) {
		if (!enlarge && !download) {
			return;
		}

		const accessibilityCopy = accessNested(this.props, 'appData.copy.accessibilityCopy', {});

		return (
			<div class={s.onesheetBtns}>
				{enlarge && (
					<div class={s.fullScreenPanel}>
						<Button class={s.fullScreenBtn} trigger={this.toggleOnesheetFullScreen} ariaLabel={accessibilityCopy.$ENLARGE_ONESHEET_ARIA}>
							<Fullscreen class={s.fullScreen} aria-hidden />
						</Button>
					</div>
				)}
				{download && (
					<div class={s.downloadPanel}>
						<Button class={s.downloadBtn} trigger={this.downloadOnesheet} ariaLabel={accessibilityCopy.$DOWNLOAD_ONESHEET_ARIA}>
							<Download class={s.download} aria-hidden />
						</Button>
					</div>
				)}
			</div>
		);
	}

	renderCredits(pageData, baseCopy) {
		const fontStyles = accessNested(this.props, 'appData.styling.fontVariables');
		const isRTL = accessNested(this.props, 'appData.styling.fontVariables.textDirection') === 'rtl';

		let credits;

		if (pageData.credits) {
			credits = sortObjectByOrder(pageData.credits);
		} else {
			// Note 28/04/2021
			// Moving over credits to their own thundr item to make it easier to add, remove, or re-order them.
			// Keeping support for existing apps but remove code below when it becomes redundant.
			const data = [
				[baseCopy.$DIRECTOR, baseCopy[pageData.directorNames] || pageData.directorNames],
				[baseCopy.$WRITER, baseCopy[pageData.writerNames] || pageData.writerNames],
				[baseCopy.$CAST, baseCopy[pageData.castNames] || pageData.castNames],
				[baseCopy.$PRODUCER, baseCopy[pageData.producerNames] || pageData.producerNames],
			];
			credits = data
				.map((credit) => {
					if (!credit[0] || !credit[1]) {
						return;
					}
					return { heading: credit[0], names: credit[1].trim() };
				})
				.filter((x) => x);
		}

		const additionalCredits = pageData.additionalCredits;
		if (additionalCredits) {
			Object.values(additionalCredits).map((credit) => {
				credits.splice(credit.position ? credit.position - 1 : credits.length, 0, credit);
			});
		}

		if (!credits.length) {
			return '';
		}

		return (
			<div class={joinClasses(s.credits, isRTL && s.rtlCredits)} ref={(ref) => (this.$credits = ref)}>
				{credits.map((credit) => (
					<div class={s.credit}>
						<HTML
							class={joinClasses(s.creditHeading, fontStyles.synopsisCredit)}
							content={getCopy(credit.heading) || credit.heading}
							markdown
							role="heading"
							aria-level="3"
						/>
						<HTML class={joinClasses(s.creditNames, fontStyles.synopsisCreditNames)} content={getCopy(credit.names) || credit.names} markdown />
					</div>
				))}
			</div>
		);
	}

	getFallbackFormat() {
		return accessNested(this.props, 'appData.pages.boltSynopsis.data.alternativeImageFormat', 'jpg');
	}

	render(props, { expanded }) {
		const currentPage = accessNested(props, ['appData', 'pages', props.id]) || {};
		const data = props.pageData || currentPage.data || {};
		const baseCopy = accessNested(props, 'appData.copy.baseCopy', {});
		const accessibilityCopy = accessNested(props, 'appData.copy.accessibilityCopy', {});
		const movieTitle = accessNested(props, 'appData.meta.title.locale') || accessNested(props, 'appData.meta.title.en');
		const active = props.store?.header?.activeSection === props.id;
		const tagline = this.tagline || baseCopy[data.tagline] || data.tagline;
		const fontStyles = accessNested(props, 'appData.styling.fontVariables');
		const onesheetAltText = accessibilityCopy[data.onesheetAltText] || data.onesheetAltText || `${movieTitle} poster`;
		const fallbackFormat = this.getFallbackFormat();
		let onesheetSrc = useDynamicAssetFolder(data.onesheet);

		const isOnesheetWithoutTagline = accessNested(props, 'client.dimensions.width') < 1024 && !tagline;

		let calculatedDetailStyle = {};

		if (this.state.detailMinHeight) {
			calculatedDetailStyle = { minHeight: `${this.state.detailMinHeight}px` };
		}

		const dedicatedPage = !props.isSection;
		const isMobile = accessNested(this.props, 'client.deviceSize', '') === 'mobile';
		const boltHome = accessNested(props, 'appData.pages.boltHome', null);
		const mobileNoHomeSynopsisFirst = !boltHome && sections[0]?.id === 'boltSynopsis' && isMobile;

		return (
			<div
				style={{ marginTop: mobileNoHomeSynopsisFirst ? getHeaderHeight() : '' }}
				class={joinClasses(s.wrapper, props.id, active && s.active, tagline && s.hasTagline, dedicatedPage && s.dedicatedPage)}
				data-section={props.id}
			>
				{this.renderBgContent()}
				{tagline && (
					<div id="synopsis-tagline" class={s.tagline} ref={(e) => (this.$tagline = e)}>
						{tagline.match(/\.(webp|jpeg|jpg|gif|png)$/) ? (
							<img src={tagline} alt="" />
						) : (
							<HTML tag="h3" class={fontStyles.synopsisTagline} markdown content={tagline} />
						)}
					</div>
				)}
				<div class={s.detail} ref={(e) => (this.$detail = e)} style={calculatedDetailStyle}>
					{!onesheetSrc && <div class={s.detailShadowDesktop} />}
					{onesheetSrc && (
						<div class={joinClasses(s.onesheet, expanded && s.textExpanded, isOnesheetWithoutTagline && s.onesheetPadTop)}>
							<div class={s.detailShadow} />
							<webp.Picture
								src={onesheetSrc}
								class={s.image}
								alt={onesheetAltText}
								fallbackFormat={fallbackFormat}
								ref={($ref) => (this.$onesheet = $ref)}
							/>
							{this.renderOnesheetButtons(data.enlargeOnesheet, data.downloadOnesheet, onesheetSrc, onesheetAltText)}
						</div>
					)}
					{this.renderDetails(data, baseCopy)}
				</div>
				{this.renderExpandedOnesheet(onesheetSrc, onesheetAltText)}
				{this.renderOverlayedContent()}
			</div>
		);
	}
}
